import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ES_ES_FALLBACK_REPLACEMENTS, ES_ES_MESSAGES } from './messages'

export const SUPPORTED_LOCALES = ['pt-BR', 'es-ES'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

const DEFAULT_LOCALE: AppLocale = 'pt-BR'
const LOCALE_STORAGE_KEY = 'propalar-locale'
const DOCUMENT_DEFAULT_LANG = 'pt-BR'
const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

type TranslationValue = string | number | bigint
type TranslationParams = Record<string, TranslationValue>

function translatePtBrToEsEsFallback(input: string): string {
  let output = input

  for (const [pattern, replacement] of ES_ES_FALLBACK_REPLACEMENTS) {
    output = output.replace(pattern, replacement)
  }

  return output
}

function isSupportedLocale(locale: string): locale is AppLocale {
  return SUPPORTED_LOCALES.includes(locale as AppLocale)
}

function detectBrowserLocale(): AppLocale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const primary = navigator.languages?.[0] ?? navigator.language ?? DEFAULT_LOCALE
  if (primary.toLowerCase().startsWith('es')) return 'es-ES'
  return DEFAULT_LOCALE
}

function readPersistedLocale(): AppLocale | null {
  if (typeof window === 'undefined') return null
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
  if (!stored) return null
  return isSupportedLocale(stored) ? stored : null
}

function getInitialLocale(): AppLocale {
  return readPersistedLocale() ?? detectBrowserLocale()
}

function applyLocaleSideEffects(locale: AppLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale || DOCUMENT_DEFAULT_LANG
  }
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  }
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template

  return template.replace(VARIABLE_PATTERN, (_, variableName: string) => {
    const value = params[variableName]
    return value == null ? '' : String(value)
  })
}

function getLocalizedTemplate(locale: AppLocale, key: string): string {
  if (locale === 'es-ES') {
    return ES_ES_MESSAGES[key] ?? translatePtBrToEsEsFallback(key)
  }

  return key
}

let activeLocale: AppLocale = getInitialLocale()

export function translate(key: string, params?: TranslationParams): string {
  const template = getLocalizedTemplate(activeLocale, key)
  return interpolate(template, params)
}

interface I18nContextValue {
  locale: AppLocale
  setLocale: (locale: AppLocale) => void
  t: (key: string, params?: TranslationParams) => string
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

interface I18nProviderProps {
  children: React.ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<AppLocale>(activeLocale)

  useEffect(() => {
    activeLocale = locale
    applyLocaleSideEffects(locale)
  }, [locale])

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale: setLocaleState,
      t: (key, params) => {
        const template = getLocalizedTemplate(locale, key)
        return interpolate(template, params)
      },
    }),
    [locale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext)

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider')
  }

  return context
}
